import React from "react";
import styles from "./PDFExport.module.scss";
import { TrainedIndicator } from "generated/graphql";
import { PartialDeep } from "type-fest";
import tinygradient from "tinygradient";
import { IndicatorGradientColors } from "@pages/pdf-export/PDFExport";
import { GetIndices, IndicatorNameMatches } from "@pages/pdf-export/Indices";

interface Page5Props {
  indicators: PartialDeep<TrainedIndicator, { recurseIntoArrays: true }>[];
}

const OverrideNames: Record<string, string> = {
  "OM LOI": "Soil Organic Matter (LoI)",
  "OM Dumas": "Soil Organic Matter (Dumas)",
};

const AllowedIndicators = [
  "Microbial Biomass",
  "Respiration",
  "Metabolic quotient",
  "OM LOI",
  "WHC water content",
  "Field water content",
  "Nitrate nitrogen",
  "Ammonium nitrogen",
  "pH",
  "P",
  "K",
  "Mg",
  "OM Dumas",
  "Total nitrogen",
  "Total carbon",
  "Soil organic carbon",
  "CN ratio",
  "Bulk density",
];

const Page5: React.FC<Page5Props> = ({ indicators }) => {
  const gradient = tinygradient(IndicatorGradientColors);

  const getIndicatorDefinitionName = (
    indicator: PartialDeep<TrainedIndicator, { recurseIntoArrays: true }>
  ) => indicator.indicatorDefinition?.name ?? indicator.name ?? "";

  const getIndicatorNames = (
    indicator: PartialDeep<TrainedIndicator, { recurseIntoArrays: true }>
  ) =>
    [
      indicator.name,
      indicator.indicatorDefinition?.name,
      indicator.indicatorDefinition?.displayName,
    ].filter(Boolean) as string[];

  const getDisplayName = (
    indicator: PartialDeep<TrainedIndicator, { recurseIntoArrays: true }>
  ) => {
    const definitionName = getIndicatorDefinitionName(indicator);

    return (
      OverrideNames[definitionName] ??
      OverrideNames[indicator.name ?? ""] ??
      indicator.indicatorDefinition?.displayName ??
      definitionName
    );
  };

  const uniqueIndicators = indicators.filter(
    (indicator, index, self) =>
      index ===
        self.findIndex(
          (t) =>
            IndicatorNameMatches(
              getIndicatorDefinitionName(t),
              getIndicatorDefinitionName(indicator)
            )
        ) &&
      getIndicatorNames(indicator).some((name) =>
        AllowedIndicators.some((allowed) => IndicatorNameMatches(name, allowed))
      )
  );

  return (
    <div className="">
      <h3>Indicator Information</h3>
      {uniqueIndicators
        .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)))
        .map((indicator) => {
          const indices = GetIndices(indicator);
          return (
            <div key={indicator.id} style={{ breakInside: "avoid" }}>
              <h4>{getDisplayName(indicator)}</h4>
              {indicator.indicatorDefinition?.unit && (
                <p>Unit: {indicator.indicatorDefinition?.unit}</p>
              )}
              <p>{indicator.indicatorDefinition?.description}</p>
              <p>{indices.indexName} Indices:</p>
              <div className={styles.indicatorInfoContainer}>
                {indices.breakpoints.map((breakpoint, index) => (
                  <div className={styles.indicatorInfoItem} key={index}>
                    <div
                      style={{
                        backgroundColor: gradient
                          .rgbAt(index / (indices.breakpoints.length - 1))
                          .setAlpha(0.7)
                          .toHex8String(),
                      }}
                      className={`${styles.indicatorInfoItemHeader} ${
                        gradient
                          .rgbAt(index / (indices.breakpoints.length - 1))
                          .isLight()
                          ? ""
                          : styles.light
                      }`}
                    >
                      {indices.names[index]}
                    </div>
                    <div className={styles.indicatorInfoItemBody}>
                      {index === 0 && (
                        <>
                          &lt;{" "}
                          {indices.displayValues[0].end.toFixed(
                            indicator.indicatorDefinition?.displayDecimalPlaces!
                          )}
                        </>
                      )}
                      {index === indices.breakpoints.length - 1 && (
                        <>
                          &gt;={" "}
                          {indices.displayValues[index - 1].end.toFixed(
                            indicator.indicatorDefinition?.displayDecimalPlaces!
                          )}
                        </>
                      )}
                      {index !== 0 &&
                        index !== indices.breakpoints.length - 1 && (
                          <>
                            {indices.displayValues[index].start.toFixed(
                              indicator.indicatorDefinition
                                ?.displayDecimalPlaces!
                            )}
                            {" - "}
                            {indices.displayValues[index].end.toFixed(
                              indicator.indicatorDefinition
                                ?.displayDecimalPlaces!
                            )}
                          </>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default Page5;
